/**
 * Create Role Use Case
 *
 * Handles role creation with business logic validation.
 * Currently delegates to repository, but provides extension points for:
 * - Permission validation (user can't assign perms they don't have)
 * - Duplicate name checking
 * - Auditing and logging
 * - Event publishing (role.created)
 *
 * @module domains/personnel/application/use-cases/CreateRoleUseCase
 */

import type { IRoleApiPort } from '@personnel/application/ports/IRoleApiPort';
import type { RoleInputDto } from '@personnel/application/dtos/RoleInputDto';
import type { Role } from '@personnel/domain/entities/Role';

/**
 * Creates a use case to create a new role
 *
 * @param props - Dependencies
 * @param props.apiPort - Role API port implementation
 * @returns Use case with execute method
 *
 * @example
 * const apiPort = createRoleHttpClient({ httpClient: connectApi });
 * const useCase = createRoleUseCase({ apiPort });
 * const newRole = await useCase.execute({
 *   name: 'Manager',
 *   permissions: ['org-menu', 'org-personnel']
 * });
 */
export const createRoleUseCase = (props: { apiPort: IRoleApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes role creation
   *
   * @param dto - Role creation data
   * @returns Promise resolving to created Role entity
   * @throws Error if validation fails or repository call fails
   *
   * @example
   * const role = await execute({
   *   name: 'Cashier',
   *   permissions: ['store-pos-open-close']
   * });
   */
  const execute = async (dto: RoleInputDto): Promise<Role> => {
    // Future business logic could go here:
    // - Validate user has permission to create roles
    // - Validate user can only assign permissions they have
    // - Check for duplicate role names
    // - Validate permission combinations
    // - Log audit trail
    // - Publish domain events

    const role = await apiPort.createRole(dto);

    return role;
  };

  return {
    execute,
  };
};
